/**
 * Expense Categories for Irish Contractors
 *
 * These categories are aligned with Irish Revenue guidelines for allowable business expenses.
 * Each category includes tax deductibility information and guidance.
 */

// Tax deductibility levels
export const DEDUCTIBILITY = {
  FULL: "full", // 100% deductible
  PARTIAL: "partial", // Partially deductible (e.g., 50% for meals)
  CONDITIONAL: "conditional", // Deductible if conditions met
  NONE: "none", // Not deductible
};

// Expense categories with Irish tax guidance
export const EXPENSE_CATEGORIES = [
  {
    value: "Office",
    label: "🏢 Office Supplies",
    nominalCode: "7000",
    deductibility: DEDUCTIBILITY.FULL,
    vatReclaimable: true,
    guidance: "Stationery, printer ink, office furniture under €1,000",
  },
  {
    value: "Travel",
    label: "✈️ Business Travel",
    nominalCode: "7100",
    deductibility: DEDUCTIBILITY.FULL,
    vatReclaimable: true,
    guidance:
      "Flights, trains, taxis for business purposes. Keep all receipts.",
  },
  {
    value: "Motor Expenses",
    label: "🚗 Motor Expenses",
    nominalCode: "7110",
    deductibility: DEDUCTIBILITY.CONDITIONAL,
    vatReclaimable: false,
    guidance:
      "Fuel, insurance, repairs. If mixed use, only business % is deductible. Keep a mileage log.",
  },
  {
    value: "Meals & Entertainment",
    label: "🍽️ Meals & Entertainment",
    nominalCode: "7150",
    deductibility: DEDUCTIBILITY.PARTIAL,
    vatReclaimable: false,
    guidance:
      "Client entertainment is NOT deductible for Corp Tax but may be for VAT. Staff meals during travel are deductible.",
  },
  {
    value: "Software",
    label: "💻 Software & SaaS",
    nominalCode: "7400",
    deductibility: DEDUCTIBILITY.FULL,
    vatReclaimable: true,
    guidance:
      "Business software subscriptions, cloud services, development tools",
  },
  {
    value: "Marketing",
    label: "📢 Marketing & Advertising",
    nominalCode: "7200",
    deductibility: DEDUCTIBILITY.FULL,
    vatReclaimable: true,
    guidance: "Website hosting, Google Ads, LinkedIn Premium, business cards",
  },
  {
    value: "Equipment",
    label: "⚙️ Equipment & Hardware",
    nominalCode: "7300",
    deductibility: DEDUCTIBILITY.CONDITIONAL,
    vatReclaimable: true,
    guidance:
      "Laptops, monitors, phones. Items over €1,000 may need to be depreciated over 8 years.",
  },
  {
    value: "Phone & Internet",
    label: "📞 Phone & Internet",
    nominalCode: "7350",
    deductibility: DEDUCTIBILITY.CONDITIONAL,
    vatReclaimable: true,
    guidance:
      "Business phone/broadband. If personal use, only business % is deductible.",
  },
  {
    value: "Professional Services",
    label: "👔 Professional Services",
    nominalCode: "7500",
    deductibility: DEDUCTIBILITY.FULL,
    vatReclaimable: true,
    guidance: "Accountant fees, legal advice, consulting services",
  },
  {
    value: "Subscriptions",
    label: "🔑 Subscriptions & Memberships",
    nominalCode: "7410",
    deductibility: DEDUCTIBILITY.FULL,
    vatReclaimable: true,
    guidance:
      "Professional body memberships, industry publications, online tools",
  },
  {
    value: "Education",
    label: "🎓 Training & Education",
    nominalCode: "7550",
    deductibility: DEDUCTIBILITY.CONDITIONAL,
    vatReclaimable: true,
    guidance:
      "Courses, conferences, books related to your current trade. New skills may not qualify.",
  },
  {
    value: "Home Office",
    label: "🏠 Home Office",
    nominalCode: "7600",
    deductibility: DEDUCTIBILITY.PARTIAL,
    vatReclaimable: false,
    guidance:
      "Proportion of rent/mortgage interest, utilities, broadband. Calculate based on room area and time used.",
  },
  {
    value: "Insurance",
    label: "🛡️ Business Insurance",
    nominalCode: "7700",
    deductibility: DEDUCTIBILITY.FULL,
    vatReclaimable: false,
    guidance:
      "Professional indemnity, public liability, employer's liability insurance",
  },
  {
    value: "Pension",
    label: "💰 Pension Contributions",
    nominalCode: "7750",
    deductibility: DEDUCTIBILITY.FULL,
    vatReclaimable: false,
    guidance:
      "Company pension contributions are fully deductible. Age-based limits apply for personal contributions.",
  },
  {
    value: "Bank & Payment Fees",
    label: "🏦 Bank & Payment Fees",
    nominalCode: "7900",
    deductibility: DEDUCTIBILITY.FULL,
    vatReclaimable: false,
    guidance: "Bank charges, Stripe/PayPal fees, currency conversion fees",
  },
  {
    value: "Legal Fees",
    label: "⚖️ Legal Fees",
    nominalCode: "7950",
    deductibility: DEDUCTIBILITY.CONDITIONAL,
    vatReclaimable: true,
    guidance:
      "Contract review, debt collection. Capital legal fees (e.g., property purchase) are not deductible.",
  },
  {
    value: "Revenue Payments",
    label: "🏛️ Revenue Payments",
    nominalCode: "2100",
    deductibility: DEDUCTIBILITY.NONE,
    vatReclaimable: false,
    guidance:
      "VAT, PAYE, Corporation Tax payments. These are not expenses but tax liabilities.",
  },
  {
    value: "Client Expenses",
    label: "🤝 Reimbursable Client Expenses",
    nominalCode: "7850",
    deductibility: DEDUCTIBILITY.FULL,
    vatReclaimable: true,
    guidance: "Expenses incurred on behalf of clients that will be recharged",
  },
  {
    value: "Utilities",
    label: "💡 Utilities",
    nominalCode: "7800",
    deductibility: DEDUCTIBILITY.CONDITIONAL,
    vatReclaimable: true,
    guidance:
      "Electricity, gas, water for business premises. Home office - use proportional claim.",
  },
  {
    value: "Small Benefit Exemption",
    label: "🎁 Small Benefit Exemption",
    nominalCode: "7760",
    deductibility: DEDUCTIBILITY.FULL,
    vatReclaimable: true,
    guidance:
      "Tax-free benefits up to €1,500/year per employee (5 vouchers max). Must be non-cash (gift cards, vouchers). Cannot be converted to cash. Fully deductible for the company.",
  },
  {
    value: "Other",
    label: "📝 Other Expenses",
    nominalCode: "9999",
    deductibility: DEDUCTIBILITY.CONDITIONAL,
    vatReclaimable: true,
    guidance:
      "Miscellaneous business expenses. Ensure you can justify the business purpose.",
  },
  {
    value: "payroll",
    label: "💸 Payroll",
    guidance:
      "Salaries, wages, bonuses, and other employee compensation. Includes PAYE/PRSI/USC liabilities.",
    deductibility: DEDUCTIBILITY.FULL, // Generally fully deductible
    vatReclaimable: false, // No VAT on payroll
  },
  {
    value: "Directors Loan",
    label: "💼 Director's Loan",
    nominalCode: "2300",
    deductibility: DEDUCTIBILITY.NONE,
    vatReclaimable: false,
    guidance:
      "Funds introduced to or withdrawn from the company by the director. Not a tax-deductible expense.",
  },
  {
    value: "Charity Donation",
    label: "💖 Charity Donation",
    nominalCode: "8200",
    deductibility: DEDUCTIBILITY.CONDITIONAL,
    vatReclaimable: false,
    guidance:
      "Donations to approved charities are tax-deductible for the company. Obtain a receipt from the charity.",
  },
];

// Simple array for dropdowns (backwards compatible)
export const CATEGORY_OPTIONS = EXPENSE_CATEGORIES.map((cat) => cat.value);

// Labeled options for Select components
export const CATEGORY_SELECT_OPTIONS = EXPENSE_CATEGORIES.map((cat) => ({
  label: cat.label,
  value: cat.value,
}));
